// ========== src/components/Modals/AddChildModal.jsx ==========

import { useState } from 'react';
import * as api from '../../utils/api';

const AVATARS = ['👦', '👧', '🧒', '👶', '🎓', '📚', '🌟', '🎨', '⚽', '🎮'];
const GRADES = ['1 klasė', '2 klasė', '3 klasė', '4 klasė', '5 klasė', '6 klasė', '7 klasė', '8 klasė', '9 klasė', '10 klasė', '11 klasė', '12 klasė'];

const AddChildModal = ({ isOpen, onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        password: '',
        age: '',
        grade: '',
        avatar: '👦'
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            if (!formData.name || formData.name.length < 2) {
                throw new Error('Vardas turi būti bent 2 simbolių ilgio');
            }

            if (!formData.password || formData.password.length < 6) {
                throw new Error('Slaptažodis turi būti bent 6 simbolių ilgio');
            }

            const childData = {
                name: formData.name,
                password: formData.password,
                avatar: formData.avatar
            };

            if (formData.age) childData.age = parseInt(formData.age);
            if (formData.grade) childData.grade = formData.grade;

            const result = await api.addChild(childData);

            if (result.success) {
                const addedPassword = formData.password;
                setFormData({ name: '', password: '', age: '', grade: '', avatar: '👦' });
                if (onSuccess) {
                    onSuccess({
                        ...result.child,
                        password: addedPassword
                    });
                }
            } else {
                // detail can be a string or an object {code, message}
                const detail = result.detail;
                if (detail && typeof detail === 'object' && detail.message) {
                    throw new Error(detail.message);
                }
                throw new Error(typeof detail === 'string' ? detail : 'Nepavyko pridėti vaiko');
            }
        } catch (err) {
            setError(err.message || 'Įvyko klaida pridėdant vaiką');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative">
                <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl font-bold">×</button>

                <h2 className="text-3xl font-bold text-gray-800 mb-6">Pridėti vaiką</h2>

                {error && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg mb-4">
                        <p>{error}</p>
                        {error.includes('planas') && (
                            <a
                                href="/subscription"
                                className="inline-block mt-2 text-sm font-bold text-red-800 underline hover:text-red-900"
                            >
                                → Atnaujinti planą
                            </a>
                        )}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Vardas *</label>
                        <input
                            type="text"
                            value={formData.name}
                            onChange={(e) => handleChange('name', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
                            placeholder="Pvz., Jonas"
                            required
                            minLength={2}
                            maxLength={50}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Slaptažodis *</label>
                        <input
                            type="password"
                            value={formData.password}
                            onChange={(e) => handleChange('password', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
                            placeholder="Bent 6 simboliai"
                            required
                            minLength={6}
                            maxLength={100}
                        />
                        <p className="text-xs text-gray-500 mt-1">Vaikas naudos šį slaptažodį prisijungimui</p>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Amžius (neprivaloma)</label>
                        <input
                            type="number"
                            value={formData.age}
                            onChange={(e) => handleChange('age', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
                            placeholder="Pvz., 8"
                            min={3}
                            max={18}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Klasė (neprivaloma)</label>
                        <select
                            value={formData.grade}
                            onChange={(e) => handleChange('grade', e.target.value)}
                            className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:border-purple-500 focus:outline-none"
                        >
                            <option value="">Pasirinkite klasę</option>
                            {GRADES.map(grade => (
                                <option key={grade} value={grade}>{grade}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">Pasirinkite avatarą</label>
                        <div className="grid grid-cols-5 gap-2">
                            {AVATARS.map(avatar => (
                                <button
                                    key={avatar}
                                    type="button"
                                    onClick={() => handleChange('avatar', avatar)}
                                    className={`text-4xl p-3 rounded-xl transition transform hover:scale-110 ${formData.avatar === avatar ? 'bg-purple-500 shadow-lg' : 'bg-gray-100 hover:bg-gray-200'
                                        }`}
                                >
                                    {avatar}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-300 transition"
                            disabled={loading}
                        >
                            Atšaukti
                        </button>
                        <button
                            type="submit"
                            className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl font-bold hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={loading}
                        >
                            {loading ? 'Pridedama...' : 'Pridėti'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AddChildModal;
